import React, { useEffect, useState } from "react";
import { MessageCircle, X, Mail, ExternalLink, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

const DISMISS_KEY = "bl_contact_floater_dismissed";
const HIDE_DAYS = 30; // dismiss lasts 30 days
const CONTACT_EMAIL = "BudLink@LinkLitePro.com";

/**
 * Bottom-right dismissible "Contact us" floater. Non-intrusive:
 *   • Starts collapsed as a small pill button.
 *   • Click to expand; click X to close the panel.
 *   • Click "Don't show again" to hide the entire floater for 30 days
 *     (persisted in localStorage). Comes back after that so the founder
 *     can still be reached long-term.
 *   • On submit, opens the user's mail client with a prefilled subject +
 *     body — no backend call needed, no extra endpoint to maintain.
 */
export const ContactFloater = () => {
  const [dismissed, setDismissed] = useState(true); // start hidden, decide in effect
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [msg, setMsg] = useState("");
  // Dim the floater while the user is typing anywhere on the page — the
  // solid-emerald button was covering the "In stock right now only" toggle
  // on mobile. Tracking `focusin`/`focusout` at the document level catches
  // every input/textarea/select without each parent having to opt in.
  const [inputFocused, setInputFocused] = useState(false);
  useEffect(() => {
    const isFormField = (el) => {
      if (!el) return false;
      const tag = (el.tagName || "").toLowerCase();
      return (
        tag === "input" ||
        tag === "textarea" ||
        tag === "select" ||
        el.isContentEditable
      );
    };
    // Ignore focus that happens INSIDE the floater's own contact form —
    // otherwise the panel dims itself while the user types their message.
    const insideFloater = (el) =>
      !!el?.closest?.("[data-testid='contact-floater']");
    const onIn = (e) => {
      if (isFormField(e.target) && !insideFloater(e.target)) setInputFocused(true);
    };
    const onOut = (e) => {
      if (isFormField(e.target) && !insideFloater(e.target)) setInputFocused(false);
    };
    document.addEventListener("focusin", onIn);
    document.addEventListener("focusout", onOut);
    return () => {
      document.removeEventListener("focusin", onIn);
      document.removeEventListener("focusout", onOut);
    };
  }, []);

  // Load dismiss state from localStorage — respect the 30-day snooze.
  useEffect(() => {
    try {
      const raw = localStorage.getItem(DISMISS_KEY);
      if (!raw) {
        setDismissed(false);
        return;
      }
      const until = Number(raw);
      if (Number.isFinite(until) && until > Date.now()) {
        setDismissed(true);
      } else {
        localStorage.removeItem(DISMISS_KEY);
        setDismissed(false);
      }
    } catch {
      setDismissed(false);
    }
  }, []);

  const snooze = () => {
    try {
      localStorage.setItem(
        DISMISS_KEY,
        String(Date.now() + HIDE_DAYS * 24 * 60 * 60 * 1000),
      );
    } catch {
      /* private mode — nothing to persist, just hide for this session */
    }
    setDismissed(true);
    setOpen(false);
  };

  const send = (e) => {
    e.preventDefault();
    const body = msg.trim();
    if (!body) {
      toast.error("Add a short message first");
      return;
    }
    const nameLine = name.trim() ? `From: ${name.trim()}\n\n` : "";
    const subject = "Bud Link — Message from a visitor";
    const url =
      `mailto:${CONTACT_EMAIL}` +
      `?subject=${encodeURIComponent(subject)}` +
      `&body=${encodeURIComponent(nameLine + body)}`;
    window.location.href = url;
    toast.success("Opening your email app…");
  };

  if (dismissed) return null;

  return (
    <div
      data-testid="contact-floater"
      className={`fixed bottom-4 right-4 z-40 sm:bottom-6 sm:right-6 transition-opacity duration-200 ${
        inputFocused && !open
          ? "opacity-15 pointer-events-none"
          : "opacity-100"
      }`}
    >
      {open ? (
        <div
          data-testid="contact-floater-panel"
          className="w-[320px] sm:w-[360px] rounded-2xl border border-border bg-card shadow-2xl backdrop-blur-md overflow-hidden animate-in fade-in slide-in-from-bottom-4 duration-200"
        >
          <div className="px-5 py-3.5 flex items-center justify-between border-b border-border/60 bg-secondary/40">
            <div className="flex items-center gap-2">
              <MessageCircle className="w-4 h-4 text-[#d4a268]" strokeWidth={1.8} />
              <span className="font-serif text-base">How can we help?</span>
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              data-testid="contact-floater-close"
              aria-label="Close contact panel"
              className="text-foreground/60 hover:text-foreground -mr-1 p-1"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          <form onSubmit={send} className="p-5 space-y-3">
            <p className="text-xs text-foreground/70 leading-relaxed">
              Feedback, a shop that&apos;s missing, a bug you spotted — send
              a note and we&apos;ll get back within a business day.
            </p>
            <Input
              data-testid="contact-floater-name"
              placeholder="Your name (optional)"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={80}
              className="h-9 text-sm"
            />
            <Textarea
              data-testid="contact-floater-message"
              placeholder="What's on your mind?"
              value={msg}
              onChange={(e) => setMsg(e.target.value)}
              maxLength={800}
              rows={4}
              className="text-sm resize-none"
            />
            <div className="flex items-center justify-between gap-2 pt-1">
              <a
                href={`mailto:${CONTACT_EMAIL}`}
                data-testid="contact-floater-direct"
                className="inline-flex items-center gap-1 text-[11px] text-foreground/60 hover:text-foreground"
              >
                <Mail className="w-3 h-3" />
                {CONTACT_EMAIL}
                <ExternalLink className="w-2.5 h-2.5" />
              </a>
              <Button
                type="submit"
                size="sm"
                data-testid="contact-floater-send"
                className="rounded-full h-8 bg-primary text-primary-foreground hover:bg-primary/90"
              >
                <Send className="w-3.5 h-3.5 mr-1.5" />
                Send
              </Button>
            </div>
          </form>

          <button
            type="button"
            onClick={snooze}
            data-testid="contact-floater-snooze"
            className="w-full text-[10px] uppercase tracking-[0.18em] text-foreground/50 hover:text-foreground/80 border-t border-border/60 py-2.5 transition-colors"
          >
            Don&apos;t show this again
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setOpen(true)}
          data-testid="contact-floater-toggle"
          className="group relative inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground shadow-xl hover:bg-primary/90 pl-4 pr-5 h-11 transition-all"
          aria-label="Contact us"
        >
          <span
            onClick={(e) => {
              e.stopPropagation();
              snooze();
            }}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.stopPropagation();
                snooze();
              }
            }}
            data-testid="contact-floater-quick-x"
            aria-label="Hide the contact floater"
            className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-card border border-border text-foreground/70 hover:text-foreground flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
          >
            <X className="w-3 h-3" />
          </span>
          <MessageCircle className="w-4 h-4" strokeWidth={1.8} />
          <span className="text-sm">Contact us</span>
        </button>
      )}
    </div>
  );
};

export default ContactFloater;
